/**
 * "Autobooks Employees" sign-in from a tenant frontend site. Autobooks accounts are
 * SSO-only and tenant hosts cannot host Entra SSO, so the browser is sent to the
 * admin site's sso/frontend-login, which signs in with Microsoft and returns to
 * /ab-login on this host with a one-time handoff code. The page the user was trying
 * to reach, and a random binding value the code must be redeemed with, are kept in
 * this tab's sessionStorage across the round-trip.
 */
const returnPathKey = 'abSignInReturnPath';
const bindingKey = 'abSignInBinding';

/**
 * The admin site that signs in users for this frontend host. Staging tenant URLs
 * (*.dev.rcgiltner.com) use the staging admin site; mirrors EfRepositoryClient.ManageUrl.
 */
export function adminSiteUrl(): string {
    const host = window.location.hostname.toLowerCase();
    if(host.endsWith('.dev.rcgiltner.com'))
        return 'https://s1.rcgiltner.com';
    if(host === 'localhost' || host.endsWith('.local'))
        return 'http://localhost:1146';
    return 'https://manage.rcgiltner.com';
}

function randomHex(bytes: number): string {
    const values = new Uint8Array(bytes);
    window.crypto.getRandomValues(values);
    return Array.from(values, b => b.toString(16).padStart(2, '0')).join('');
}

/** Leave for Microsoft sign-in, returning to `returnPath` on this site afterwards. */
export function startAbSignIn(returnPath: string): void {
    // A handoff code minted for this sign-in is only redeemable with this tab's binding,
    // so a forwarded sign-in link can't sign another browser in as this user.
    const binding = randomHex(16);
    try {
        sessionStorage.setItem(returnPathKey, returnPath);
        sessionStorage.setItem(bindingKey, binding);
    } catch(e) { }
    window.location.assign(`${adminSiteUrl()}/api/sso/frontend-login`
        + `?host=${encodeURIComponent(window.location.host)}&binding=${binding}`);
}

/** Return (once) the binding saved by startAbSignIn, if this tab started a sign-in. */
export function takeAbSignInBinding(): string | null {
    try {
        const binding = sessionStorage.getItem(bindingKey);
        sessionStorage.removeItem(bindingKey);
        return binding;
    } catch(e) {
        return null;
    }
}

/** Return (once) where to go after sign-in: a same-site path, never a sign-in page. */
export function takeAbSignInReturnPath(): string {
    let path: string | null = null;
    try {
        path = sessionStorage.getItem(returnPathKey);
        sessionStorage.removeItem(returnPathKey);
    } catch(e) { }

    if(!path || !path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')
        || /^\/(login|ab-login|logoff)(\/|\?|#|$)/i.test(path))
        return '/';
    return path;
}
